import { Injectable } from '@angular/core';
import { DeviceDetectorService } from 'ngx-device-detector';
import { AboutVoatzApi, ApiAboutService } from   '../Services/api-about.service' 
import { BehaviorSubject } from 'rxjs';
import { Platform } from '@angular/cdk/platform';
import { SessionStorageService } from '../Services/session-storage.service';
import { DeviceProfile } from '../backend-types/misc-types';

interface BrowserSupportMatrix {
  desktop: { [b: string]: BrowserSupportStatus };
  mobile: { [b: string]: BrowserSupportStatus };
  tablet: { [b: string]: BrowserSupportStatus };
}

interface BrowserSupportStatus {
  name: string;
  minMajor: number;
  incompatibleFeatures: Feature[];
}

type Feature = 'affidavit' | 'headshot';

export const browserSupportMatrix: BrowserSupportMatrix = {
  desktop: {
    Chrome: { name: $localize`Chrome`, minMajor: 125, incompatibleFeatures: [] },
    'MS-Edge-Chromium': { name: $localize`Edge`, minMajor: 125, incompatibleFeatures: [] },
    Firefox: { name: $localize`Firefox`, minMajor: 127, incompatibleFeatures: [] },
    Safari: { name: $localize`Safari`, minMajor: 14, incompatibleFeatures: [] },
  },
  mobile: {
    Chrome: { name: $localize`Chrome`, minMajor: 125, incompatibleFeatures: [ ] },
    Firefox: { name: $localize`Firefox`, minMajor: 127, incompatibleFeatures: [ ] },
    Safari: { name: $localize`Safari`, minMajor: 14, incompatibleFeatures: [ ] },
    'MS-Edge': { name: $localize`Edge`, minMajor: 125, incompatibleFeatures: [] },
    Samsung: { name: $localize`Samsung`, minMajor: 23, incompatibleFeatures: [ ] }
  },
  tablet: {
    Chrome: { name: $localize`Chrome`, minMajor: 125, incompatibleFeatures: [ ] },
    Safari: { name: $localize`Safari`, minMajor: 14, incompatibleFeatures: [ ] },
  }
};

@Injectable({
  providedIn: 'root'
})
export class BrowserSupportService {

  readonly supportedBrowserNames = new BehaviorSubject<string[]>([]);
  readonly isBrowserSupported = new BehaviorSubject<boolean>(true);
  readonly switchCameraSupported: boolean;
  readonly resizeCanvasSupported: boolean;
  readonly deviceProfile: DeviceProfile;
  readonly device;

  constructor(
    private readonly deviceService: DeviceDetectorService,
    private readonly aboutService: ApiAboutService,
    private platform: Platform,
    private storer: SessionStorageService
  ) {
    this.device = this.deviceService.getDeviceInfo();
    this.deviceProfile = this.getDeviceProfile();
    const aboutInfo = this.aboutService.aboutInformation.getValue()
    if (this.device.deviceType === 'mobile' || this.device.deviceType === 'desktop' || this.device.deviceType === 'tablet') {
      const browsers = browserSupportMatrix[this.device.deviceType];
      const browser = browsers[this.device.browser];
      this.isBrowserSupported.next(browser !== undefined &&
        parseMajor(this.device.browser_version) >= browser.minMajor &&
        !this.hasConflictingFeaturesFor(browser, aboutInfo)
      );
      this.supportedBrowserNames.next(
        Object.values(browsers).filter(b => !this.hasConflictingFeaturesFor(b, aboutInfo)).map(v => v.name)
      );
    } else {
      this.isBrowserSupported.next(false);
      this.supportedBrowserNames.next([]);
    }
    this.resizeCanvasSupported = this.device.deviceType === 'mobile';
    this.switchCameraSupported = !(this.device.deviceType === 'desktop');
  }

  private hasConflictingFeaturesFor(browser: BrowserSupportStatus, about: AboutVoatzApi): boolean {
    return !browser.incompatibleFeatures.every(f => !isFeatureEnabled(f, about));
  }

  getDeviceProfile(): DeviceProfile {
    let deviceProfile: DeviceProfile = {initialNextKey: 0};
    
    deviceProfile.os = this.device.os;
    deviceProfile.osVersion = this.device.os_version;
    deviceProfile.deviceModel = this.getBrowserName()+" "+this.device.browser_version;
    this.storer.setDeviceProfile(deviceProfile);

    return deviceProfile;
  }
  
  getBrowserName(): string {
    if ( this.platform.BLINK ){
      return "Chrome";
    } else if ( this.platform.EDGE ){
      return "Edge";
    } else if ( this.platform.FIREFOX ) {
      return "Firefox";
    } else if ( this.platform.SAFARI ) {
      return "Safari";
    } else if ( this.platform.TRIDENT ) {
      return "Trident";
    } else if ( this.platform.WEBKIT ) {
      return "Webkit";
    } else {
      return this.device.browser;
    }
  }
}

function parseMajor(versionString: string): number {
  return parseInt(versionString.split('.')[0], 10);
}

function isFeatureEnabled(f: Feature, a: AboutVoatzApi): boolean {
  if (f === 'affidavit') {
    return a.voter_affidavit;
  } else {
    return true;
  }
}